import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AllInclusiveIcon,
  ArrowUpwardIcon,
  AutoAwesomeIcon,
  ChatBubbleOutlineIcon,
  CheckIcon,
  FileTextIcon,
  InfoIcon,
  NorthEastIcon,
  PlayCircleIcon,
  PolicyIcon,
  RefreshCwIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  VideoLibraryIcon,
} from '../icons.js';
import { apiPost, ApiError } from '../../api/client.js';
import { vfmt } from '../../lib/video-format.js';

/**
 * "Ask the trainer" panel — three states: empty / thinking / answered.
 *
 *   - Scope tabs at the top: "This video" / "All training videos" /
 *     "Whole knowledge base". Set by the parent page.
 *   - Empty state: composer + 3 suggested prompts.
 *   - Thinking state: spinner + the question echoed back.
 *   - Answered state: question echo + cited answer + source list +
 *     Helpful / Not quite feedback.
 *
 * Citations come back from the agent in two shapes:
 *   - Video citations have `sourceType === 'video'` + videoId + startSeconds.
 *     Clicking jumps the parent player (via onCiteJump) when we're on the
 *     same video, otherwise navigates to /videos/{videoId}?t={startSeconds}.
 *   - Document citations have the existing sourceFile/page shape. Clicking
 *     opens the document page anchor.
 */

export type AskScope = 'this' | 'videos' | 'all';

export interface AskCitation {
  chunkId: string;
  title: string;
  sourceFile?: string;
  page?: number;
  snippet: string;
  ring?: 'ring0' | 'ring1' | 'ring2';
  sourceType?: 'document' | 'video' | 'code';
  videoId?: string;
  startSeconds?: number;
  endSeconds?: number;
}

interface AskResponse {
  requestId: string;
  /** The ask persists a chat turn; these address it for /agent/feedback. */
  chatId?: string;
  messageId?: string;
  answer: string;
  citations: AskCitation[];
  outcome: string;
}

type FeedbackVerdict = 'positive' | 'negative';

interface AskPanelProps {
  /** When non-null, the panel is rendered inside a specific video's detail
   *  page; "This video" appears as the first scope tab. */
  videoId?: string;
  /** Optional explicit video title — surfaced in suggestion copy. */
  videoTitle?: string;
  /** Default scope on first mount. */
  defaultScope?: AskScope;
  /** Called when the user clicks a video citation that points at the
   *  currently-loaded video. Parent seeks the player. When the citation
   *  is for a different video, this callback is not invoked — we render
   *  it as a Link to /videos/{id}?t=… instead. */
  onCiteJump?: (videoId: string, t: number) => void;
}

export function AskPanel({
  videoId,
  videoTitle,
  defaultScope,
  onCiteJump,
}: AskPanelProps): JSX.Element {
  const [scope, setScope] = useState<AskScope>(defaultScope ?? (videoId ? 'this' : 'all'));
  const [state, setState] = useState<'empty' | 'thinking' | 'answered' | 'error'>('empty');
  const [draft, setDraft] = useState('');
  const [askedQ, setAskedQ] = useState('');
  const [response, setResponse] = useState<AskResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeCite, setActiveCite] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    verdict: FeedbackVerdict;
    status: 'saving' | 'saved' | 'error';
  } | null>(null);
  const navigate = useNavigate();

  const suggestions = useMemo(() => buildSuggestions(scope, videoTitle), [scope, videoTitle]);

  const ask = async (q: string): Promise<void> => {
    const query = (q || draft).trim();
    if (query.length === 0) return;
    setAskedQ(query);
    setDraft('');
    setState('thinking');
    setErrorMsg(null);
    setResponse(null);
    setFeedback(null);
    try {
      const body: Record<string, unknown> = {
        query,
        clientRequestId: cryptoRandomId(),
      };
      if (scope !== 'all') {
        body.videoScope = scope;
        if (scope === 'this' && videoId) body.videoScopeId = videoId;
      }
      const res = await apiPost<AskResponse>('/api/v1/agent/ask', body);
      setResponse(res);
      setState('answered');
    } catch (e) {
      setState('error');
      setErrorMsg(e instanceof ApiError ? e.message : 'Could not ask. Please try again.');
    }
  };

  const reset = (): void => {
    setState('empty');
    setActiveCite(null);
    setResponse(null);
    setFeedback(null);
  };

  // Same endpoint + payload as the Knowledge Agent's thumbs, so trainer
  // answers land in the same feedback dataset (message doc + audit row).
  const sendFeedback = async (verdict: FeedbackVerdict): Promise<void> => {
    if (!response?.chatId || !response.messageId) return;
    setFeedback({ verdict, status: 'saving' });
    try {
      await apiPost('/api/v1/agent/feedback', {
        chatId: response.chatId,
        messageId: response.messageId,
        verdict,
      });
      setFeedback({ verdict, status: 'saved' });
    } catch {
      setFeedback({ verdict, status: 'error' });
    }
  };

  // Switching scope mid-conversation clears the previous answer — the user
  // is explicitly widening or narrowing the corpus, so showing a stale
  // answer from a different scope is confusing. Composer + suggestions
  // refresh from the new scope's suggestions array.
  const changeScope = (next: AskScope): void => {
    setScope(next);
    if (state !== 'empty') reset();
  };

  return (
    <div className="flex h-full min-h-0 flex-col border-l border-nebula-border-soft bg-white">
      {/* Header + scope tabs */}
      <div className="border-b border-nebula-border-soft px-4 pb-2.5 pt-3.5">
        <div className="mb-2.5 flex items-center gap-2">
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white"
            style={{
              background:
                'linear-gradient(135deg, var(--ov-secondary, #3B5AA8), var(--ov-primary, #172E57))',
            }}
          >
            <AutoAwesomeIcon className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-nebula-fg-1">Ask the trainer</div>
            <div className="text-xs text-nebula-fg-3">Grounded in the corpus you choose below.</div>
          </div>
        </div>
        <AskScopeTabs scope={scope} onChange={changeScope} hasCurrentVideo={!!videoId} />
      </div>

      {/* Body */}
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-3.5">
        {state === 'empty' && (
          <>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
              Try asking
            </div>
            <div className="flex flex-col gap-1.5">
              {suggestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => void ask(q)}
                  className="flex items-center gap-2 rounded-md border border-nebula-border-soft bg-white px-3 py-2.5 text-left text-[12.5px] font-medium text-nebula-fg-2 transition hover:bg-nebula-tint"
                >
                  <ChatBubbleOutlineIcon className="w-4 h-4 text-nebula-secondary" />
                  <span className="flex-1">{q}</span>
                  <NorthEastIcon className="w-4 h-4 text-nebula-fg-4" />
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-md border border-nebula-border-soft bg-nebula-gray-6 p-3">
              <div className="flex items-start gap-2">
                <InfoIcon className="w-4 h-4 text-nebula-secondary mt-0.5" />
                <div className="text-xs text-nebula-fg-3">{scopeHint(scope)}</div>
              </div>
            </div>
          </>
        )}

        {state === 'thinking' && <ThinkingState scope={scope} askedQ={askedQ} />}

        {state === 'error' && (
          <div className="mt-2 rounded-md border border-nebula-error bg-nebula-error-bg p-4 text-sm text-nebula-error">
            {errorMsg}
            <button onClick={reset} className="ml-2 underline">
              Try again
            </button>
          </div>
        )}

        {state === 'answered' && response && (
          <>
            <div className="mb-3 flex justify-end">
              <div className="max-w-[92%] rounded-md border border-[#DCE5FB] bg-nebula-tint px-3 py-2 text-[13px] font-medium text-nebula-fg-1">
                {askedQ}
              </div>
            </div>
            <AskProse
              text={response.answer}
              citations={response.citations}
              activeCiteId={activeCite}
              onCiteClick={(cid) => {
                setActiveCite(cid);
                const c = response.citations.find((x) => x.chunkId === cid);
                if (c?.sourceType === 'video' && c.videoId && typeof c.startSeconds === 'number') {
                  if (videoId && c.videoId === videoId && onCiteJump) {
                    onCiteJump(c.videoId, c.startSeconds);
                  } else {
                    // Mirrors the source list's Link for other videos, so an
                    // inline timestamp chip is never a click that does nothing.
                    navigate(`/videos/${c.videoId}?t=${Math.floor(c.startSeconds)}`);
                  }
                }
              }}
            />
            {response.citations.length > 0 && (
              <div className="mt-4">
                <AskSourceList
                  citations={response.citations}
                  activeCiteId={activeCite}
                  currentVideoId={videoId}
                  onCiteClick={(cid) => {
                    setActiveCite(cid);
                    const c = response.citations.find((x) => x.chunkId === cid);
                    if (
                      c?.sourceType === 'video' &&
                      c.videoId &&
                      typeof c.startSeconds === 'number'
                    ) {
                      if (videoId && c.videoId === videoId && onCiteJump) {
                        onCiteJump(c.videoId, c.startSeconds);
                      }
                    }
                  }}
                />
              </div>
            )}
            <div className="mt-4 flex items-center gap-1.5">
              {/* Hidden when the backend didn't return a persisted turn to
                  attach the verdict to — better no buttons than dead ones. */}
              {response.chatId && response.messageId && (
                <>
                  <FeedbackBtn
                    icon={<ThumbsUpIcon className="w-3.5 h-3.5" />}
                    selected={feedback?.verdict === 'positive' && feedback.status !== 'error'}
                    disabled={feedback?.status === 'saving'}
                    onClick={() => void sendFeedback('positive')}
                  >
                    Helpful
                  </FeedbackBtn>
                  <FeedbackBtn
                    icon={<ThumbsDownIcon className="w-3.5 h-3.5" />}
                    selected={feedback?.verdict === 'negative' && feedback.status !== 'error'}
                    disabled={feedback?.status === 'saving'}
                    onClick={() => void sendFeedback('negative')}
                  >
                    Not quite
                  </FeedbackBtn>
                  <span aria-live="polite" className="text-[11px] font-medium">
                    {feedback?.status === 'saved' && (
                      <span className="inline-flex items-center gap-1 text-nebula-success">
                        <CheckIcon className="w-3 h-3" />
                        Thanks
                      </span>
                    )}
                    {feedback?.status === 'error' && (
                      <span className="text-nebula-error">Couldn&apos;t save. Try again.</span>
                    )}
                  </span>
                </>
              )}
              <div className="flex-1" />
              <button
                onClick={reset}
                className="inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium text-nebula-secondary hover:bg-nebula-tint"
              >
                <RefreshCwIcon className="w-3.5 h-3.5" />
                Ask another
              </button>
            </div>
          </>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-nebula-border-soft bg-white p-3.5">
        <div className="flex items-stretch gap-1.5 rounded-md border border-nebula-border bg-white p-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim()) void ask(draft);
              }
            }}
            placeholder={
              scope === 'this'
                ? 'Ask anything about this video…'
                : scope === 'videos'
                  ? 'Ask across all training videos…'
                  : 'Ask across all training and documents…'
            }
            rows={2}
            className="flex-1 resize-none border-0 bg-transparent px-2.5 py-2 text-[13.5px] leading-[19px] text-nebula-fg-1 outline-none"
          />
          <button
            disabled={!draft.trim() || state === 'thinking'}
            onClick={() => void ask(draft)}
            className={
              draft.trim() && state !== 'thinking'
                ? 'self-end inline-flex h-9 w-9 items-center justify-center rounded-md border-0 bg-nebula-primary text-white hover:bg-brand-600'
                : 'self-end inline-flex h-9 w-9 items-center justify-center rounded-md border-0 bg-nebula-gray-4 text-white cursor-default'
            }
          >
            <ArrowUpwardIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-nebula-fg-4">
          <PolicyIcon className="w-3 h-3" />
          <span>Answers cite only what's in scope. Nothing is sent outside OneNebula.</span>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

interface AskScopeTabsProps {
  scope: AskScope;
  onChange: (s: AskScope) => void;
  hasCurrentVideo: boolean;
}

export function AskScopeTabs({ scope, onChange, hasCurrentVideo }: AskScopeTabsProps): JSX.Element {
  const tabs: { key: AskScope; label: string; icon: React.ReactNode }[] = [];
  if (hasCurrentVideo)
    tabs.push({
      key: 'this',
      label: 'This video',
      icon: <PlayCircleIcon className="w-3.5 h-3.5" />,
    });
  tabs.push({
    key: 'videos',
    label: 'All training videos',
    icon: <VideoLibraryIcon className="w-3.5 h-3.5" />,
  });
  tabs.push({
    key: 'all',
    label: 'Whole knowledge base',
    icon: <AllInclusiveIcon className="w-3.5 h-3.5" />,
  });
  return (
    <div className="inline-flex gap-0.5 rounded-md border border-nebula-border-soft bg-nebula-gray-5 p-[3px]">
      {tabs.map((t) => {
        const on = t.key === scope;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={
              on
                ? 'inline-flex items-center gap-1.5 rounded border border-nebula-border-soft bg-white px-2.5 py-1 text-xs font-semibold text-nebula-primary shadow-sm'
                : 'inline-flex items-center gap-1.5 rounded border border-transparent bg-transparent px-2.5 py-1 text-xs font-semibold text-nebula-fg-3 hover:text-nebula-fg-1'
            }
          >
            {t.icon}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Render an answer with inline citation chips. The Python agent emits
 * `[chunkId: c-abc123]` markers; we map each unique chunkId to a chip
 * numbered in order of first appearance.
 */
interface AskProseProps {
  text: string;
  citations: AskCitation[];
  activeCiteId: string | null;
  onCiteClick: (chunkId: string) => void;
}

export function AskProse({
  text,
  citations,
  activeCiteId,
  onCiteClick,
}: AskProseProps): JSX.Element {
  const idMap = new Map<string, { c: AskCitation; n: number }>();
  citations.forEach((c, i) => idMap.set(c.chunkId, { c, n: i + 1 }));

  // Match [chunkId: ...] markers
  const parts: { kind: 'text' | 'cite'; text?: string; id?: string }[] = [];
  const re = /\[chunkId:\s*([^\]]+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: 'text', text: text.slice(last, m.index) });
    parts.push({ kind: 'cite', id: m[1].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });

  return (
    <div className="prose max-w-none text-sm leading-[22px] text-nebula-fg-1">
      {parts.map((p, i) => {
        if (p.kind === 'text') return <span key={i}>{renderInlineMarkup(p.text ?? '')}</span>;
        const entry = idMap.get(p.id ?? '');
        if (!entry) return null;
        return (
          <CiteChip
            key={i}
            n={entry.n}
            cite={entry.c}
            active={activeCiteId === entry.c.chunkId}
            onClick={() => onCiteClick(entry.c.chunkId)}
          />
        );
      })}
    </div>
  );
}

function renderInlineMarkup(text: string): React.ReactNode {
  // Render \n\n as paragraph breaks and **bold** / `code` / *em* as their tags.
  const paragraphs = text.split('\n\n');
  return paragraphs.map((para, pi) => {
    const lines = para.split('\n');
    const isList = lines.every((l) => l.startsWith('- ') || l.startsWith('* ') || l.trim() === '');
    if (isList && lines.some((l) => l.startsWith('- ') || l.startsWith('* '))) {
      return (
        <ul key={pi} className="my-2 ml-5 list-disc">
          {lines
            .filter((l) => l.startsWith('- ') || l.startsWith('* '))
            .map((l, li) => (
              <li key={li}>{renderInline(l.replace(/^[-*]\s+/, ''))}</li>
            ))}
        </ul>
      );
    }
    return (
      <p key={pi} className="my-2">
        {renderInline(para)}
      </p>
    );
  });
}

function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={key++}>{m[1]}</strong>);
    else if (m[2]) out.push(<code key={key++}>{m[2]}</code>);
    else if (m[3]) out.push(<em key={key++}>{m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

interface CiteChipProps {
  n: number;
  cite: AskCitation;
  active: boolean;
  onClick: () => void;
}

function CiteChip({ n, cite, active, onClick }: CiteChipProps): JSX.Element {
  const isVideo = cite.sourceType === 'video' && cite.videoId;
  const label =
    isVideo && typeof cite.startSeconds === 'number' ? vfmt(cite.startSeconds) : `[${n}]`;
  return (
    <button
      onClick={onClick}
      title={cite.title}
      className={
        active
          ? isVideo
            ? 'mx-0.5 inline-flex items-center gap-1 rounded border-0 bg-nebula-primary px-1.5 py-0.5 align-baseline text-[11px] font-semibold text-white'
            : 'mx-0.5 inline-flex items-center gap-1 rounded border-0 bg-nebula-secondary px-1.5 py-0.5 align-baseline text-[11px] font-semibold text-white'
          : isVideo
            ? 'mx-0.5 inline-flex items-center gap-1 rounded border border-[#CADFFB] bg-nebula-info-bg px-1.5 py-0.5 align-baseline text-[11px] font-semibold text-[#1A4FB0]'
            : 'mx-0.5 inline-flex items-center gap-1 rounded border border-[#DCE5FB] bg-nebula-tint px-1.5 py-0.5 align-baseline text-[11px] font-semibold text-nebula-secondary'
      }
    >
      {isVideo ? <PlayCircleIcon className="w-3 h-3" /> : <FileTextIcon className="w-3 h-3" />}
      {label}
    </button>
  );
}

interface AskSourceListProps {
  citations: AskCitation[];
  activeCiteId: string | null;
  currentVideoId?: string;
  onCiteClick: (chunkId: string) => void;
}

export function AskSourceList({
  citations,
  activeCiteId,
  currentVideoId,
  onCiteClick,
}: AskSourceListProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="px-1 text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
        Citations · {citations.length}
      </div>
      {citations.map((c) => {
        const on = c.chunkId === activeCiteId;
        const isVideo = c.sourceType === 'video' && c.videoId;
        const target =
          isVideo && c.videoId && c.videoId !== currentVideoId
            ? `/videos/${c.videoId}?t=${Math.floor(c.startSeconds ?? 0)}`
            : null;
        const body = (
          <>
            <div
              className={
                isVideo
                  ? 'inline-flex h-12 w-20 shrink-0 items-center justify-center rounded-md bg-nebula-primary text-white'
                  : 'inline-flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-md bg-nebula-tint text-nebula-secondary'
              }
            >
              {isVideo ? (
                <PlayCircleIcon className="w-5 h-5" />
              ) : (
                <FileTextIcon className="w-5 h-5" />
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-nebula-fg-4">
                {isVideo
                  ? `Video · ${typeof c.startSeconds === 'number' ? vfmt(c.startSeconds) : ''}`
                  : `Document · p. ${c.page ?? '?'}`}
              </div>
              <div className="line-clamp-1 text-[12.5px] font-semibold text-nebula-fg-1">
                {c.title}
              </div>
              <div className="mt-0.5 line-clamp-1 text-[11.5px] text-nebula-fg-3">{c.snippet}</div>
            </div>
          </>
        );
        if (target) {
          return (
            <Link
              key={c.chunkId}
              to={target}
              className={
                on
                  ? 'flex items-stretch gap-2.5 rounded-md border border-[#DCE5FB] bg-nebula-tint p-2 text-inherit no-underline'
                  : 'flex items-stretch gap-2.5 rounded-md border border-nebula-border-soft bg-white p-2 text-inherit no-underline hover:bg-nebula-tint'
              }
            >
              {body}
            </Link>
          );
        }
        return (
          <button
            key={c.chunkId}
            onClick={() => onCiteClick(c.chunkId)}
            className={
              on
                ? 'flex items-stretch gap-2.5 rounded-md border border-[#DCE5FB] bg-nebula-tint p-2 text-left'
                : 'flex items-stretch gap-2.5 rounded-md border border-nebula-border-soft bg-white p-2 text-left hover:bg-nebula-tint'
            }
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}

// ── State helpers ────────────────────────────────────────────────────

/**
 * Starter prompts shown when the panel is empty.
 *
 * For "this video" scope we have the title and can template a useful
 * summarise/action-items prompt. For "videos" and "all" scopes we don't
 * know what content actually exists, so we keep prompts open-ended and
 * corpus-introspective ("what's in here?" rather than "tell me about
 * <invented topic X>") — they work against any knowledge base.
 */
function buildSuggestions(scope: AskScope, videoTitle?: string): string[] {
  if (scope === 'this' && videoTitle) {
    return [
      `Summarise the key points of "${videoTitle}".`,
      'What are the action items I should take from this?',
      'Are there any specific tools or systems mentioned I should learn about?',
    ];
  }
  if (scope === 'videos') {
    return [
      'What training videos are available?',
      'Summarise the topics covered across the training corpus.',
      'Which videos were added most recently?',
    ];
  }
  return [
    'What can you help me with?',
    'Summarise what topics are covered in the knowledge base.',
    'How is the knowledge base organised?',
  ];
}

function scopeHint(scope: AskScope): string {
  if (scope === 'this') {
    return "Answers come from this single video's transcript and chapter summaries. Click any timestamp to jump.";
  }
  if (scope === 'videos') {
    return "Answers come from every training video's transcripts and visual summaries. Citations jump straight to the moment.";
  }
  return 'Answers come from training videos plus the wider knowledge base (documents, Confluence, code). Citations jump to the moment or page.';
}

function ThinkingState({ scope, askedQ }: { scope: AskScope; askedQ: string }): JSX.Element {
  const lines =
    scope === 'this'
      ? ['Skimming transcript…', 'Locating chapter…', 'Drafting answer…']
      : scope === 'videos'
        ? [
            'Searching training videos…',
            'Ranking candidate moments…',
            'Drafting answer with citations…',
          ]
        : [
            'Searching training videos and the knowledge base…',
            'Cross-referencing related documents…',
            'Drafting answer…',
          ];
  return (
    <div>
      {askedQ && (
        <div className="mb-3.5 flex justify-end">
          <div className="max-w-[92%] rounded-md border border-[#DCE5FB] bg-nebula-tint px-3 py-2 text-[13px] font-medium text-nebula-fg-1">
            {askedQ}
          </div>
        </div>
      )}
      <div className="rounded-md border border-nebula-border-soft bg-nebula-gray-6 p-3.5">
        <div className="mb-2 flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 animate-pulse rounded-full bg-nebula-secondary"
            style={{ boxShadow: '0 0 0 4px rgba(59,90,168,0.18)' }}
          />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-2">
            Working
          </span>
        </div>
        <div className="flex flex-col gap-1 text-[12.5px] leading-[18px] text-nebula-fg-3">
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="text-nebula-fg-4">·</span>
              <span>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeedbackBtn({
  icon,
  selected,
  disabled,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={
        selected
          ? 'inline-flex items-center gap-1.5 rounded bg-nebula-tint px-2.5 py-1.5 text-xs font-semibold text-nebula-primary disabled:opacity-60'
          : 'inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium text-nebula-fg-3 hover:bg-nebula-gray-5 disabled:opacity-60'
      }
    >
      {icon}
      {children}
    </button>
  );
}

function cryptoRandomId(): string {
  // Falls back to a sufficiently-unique string when crypto.randomUUID is
  // missing (very old browsers). The backend's dedup window is 60s — any
  // string that changes per click is fine.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `r-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
